/**
 * Operational Insights — conclusiones automáticas accionables.
 *
 * Cada insight interpreta los datos, cuantifica el impacto económico,
 * adjunta confianza del modelo y una recomendación concreta, con
 * enlaces de drill-down a las entidades involucradas (DMA, caso,
 * cuadrilla, gateway…).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { OperationalInsight } from "../api/mock";
import { Icon } from "../components/Icon";
import { InsightCard } from "../components/InsightCard";
import { Sparkline } from "../components/Sparkline";
import { PageHeader } from "../layout/PageHeader";
import { loadInsightsBundle, kpiIntelligence } from "../lib/insightsRuntime";

const CATEGORIAS = [
  { key: "todas", label: "Todas" },
  { key: "fuga", label: "Fugas" },
  { key: "fraude", label: "Fraude" },
  { key: "infraestructura", label: "Infraestructura" },
  { key: "operacion", label: "Operación" },
  { key: "calidad_datos", label: "Calidad de datos" },
];

function clp(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `CLP ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `CLP ${(n / 1_000).toFixed(0)}k`;
  return `CLP ${n.toFixed(0)}`;
}

function timeAgo(iso: string): string {
  const diff = new Date("2025-06-04T12:00:00Z").getTime() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 60) return `hace ${m} min`;
  return `hace ${Math.round(m / 60)} h`;
}

export function Insights(): JSX.Element {
  const [cat, setCat] = useState("todas");
  const insightsQuery = useQuery<OperationalInsight[]>({
    queryKey: ["insights"],
    queryFn: () => api<OperationalInsight[]>("/insights"),
    staleTime: 60_000,
  });

  // Motor_Insights (Capa Operacional AI-First): Insights derivados por
  // correlación cross-domain sobre el Grafo_Entidades, con Narrativa_
  // Operacional, evidencia, confianza y Cadena_Causal, consumidos vía el
  // runtime selector (R5.3, R5.7).
  const motorQuery = useQuery({
    queryKey: ["motor-insights"],
    queryFn: loadInsightsBundle,
    staleTime: 60_000,
  });
  const motorInsights = motorQuery.data?.insights ?? [];

  // KPI intelligence de la confianza media (dirección + magnitud + causa),
  // calculada por el Motor_Insights vía el runtime selector (R5.5).
  const confianzaActualPct = motorInsights.length
    ? (motorInsights.reduce((a, i) => a + i.confianza, 0) / motorInsights.length) * 100
    : 0;

  const insights = useMemo(() => {
    const all = insightsQuery.data ?? [];
    const filtered = cat === "todas" ? all : all.filter((i) => i.categoria === cat);
    const sevRank = { critica: 0, alta: 1, media: 2, baja: 3 };
    return [...filtered].sort((a, b) => sevRank[a.severidad] - sevRank[b.severidad]);
  }, [insightsQuery.data, cat]);

  // Detecta si hay insights de infraestructura derivados del rf-api real.
  const liveRf = (insightsQuery.data ?? []).some((i) => i.insight_id.startsWith("RF-INS-"));

  const totalImpacto = (insightsQuery.data ?? []).reduce(
    (acc, i) => acc + (i.impacto_clp_mes ?? 0),
    0,
  );

  return (
    <>
      <PageHeader
        eyebrow="Centro de mando"
        title="Operational Insights"
        subtitle="Conclusiones automáticas: qué pasa, qué tan grave, cuánto cuesta y qué hacer."
        actions={
          <>
            {liveRf && (
              <span className="badge badge--accent" title="Insights de infraestructura derivados del rf-api (DuckDB)">
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--c-success)",
                    display: "inline-block",
                    marginRight: 5,
                  }}
                />
                EN VIVO · rf-api
              </span>
            )}
            <Link to="/casos" className="btn btn--primary">
              <Icon name="flask" size={13} /> Ver casos prioritarios
            </Link>
          </>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Insights activos</span>
          <span className="kpi__value">{insightsQuery.data?.length ?? 0}</span>
          <span className="kpi__hint">priorizados por severidad</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Impacto económico detectado</span>
          <span className="kpi__value">{clp(totalImpacto)}</span>
          <span className="kpi__hint">por mes, agregado</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Confianza media</span>
          <span className="kpi__value">
            {Math.round(
              ((insightsQuery.data ?? []).reduce((a, i) => a + i.confianza, 0) /
                Math.max(1, insightsQuery.data?.length ?? 1)) *
                100,
            )}
            %
          </span>
          <span className="kpi__hint">modelos + reglas</span>
        </div>
      </div>

      <div className="hstack" style={{ marginBottom: 12 }}>
        <div className="segmented">
          {CATEGORIAS.map((c) => (
            <button
              key={c.key}
              type="button"
              className="segmented__btn"
              aria-pressed={cat === c.key}
              onClick={() => setCat(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="vstack">
        {motorQuery.isLoading && (
          <div className="status-block status-block--info">Correlacionando señales cross-domain…</div>
        )}
        {motorInsights.length > 0 && (
          <MotorInsightsSection
            insights={motorInsights}
            confianzaActualPct={confianzaActualPct}
          />
        )}
        {insights.map((ins) => (
          <section
            key={ins.insight_id}
            className="card"
            style={{ borderLeft: `3px solid ${
              ins.severidad === "critica"
                ? "var(--c-danger)"
                : ins.severidad === "alta"
                  ? "var(--c-warn)"
                  : ins.severidad === "media"
                    ? "var(--c-info)"
                    : "var(--c-border-strong)"
            }` }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 16,
                alignItems: "start",
              }}
            >
              <div>
                <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
                  <span className={`sev-pill sev-pill--${ins.severidad}`}>{ins.severidad}</span>
                  <span className="badge badge--neutral">{ins.categoria.replace("_", " ")}</span>
                  <span className="text-faint" style={{ fontSize: 11 }}>{timeAgo(ins.detectado_en)}</span>
                </div>
                <h3 style={{ marginBottom: 6 }}>{ins.titulo}</h3>
                <p style={{ color: "var(--c-text)", fontSize: 13, marginBottom: 10 }}>
                  {ins.detalle}
                </p>
                <div
                  className="status-block status-block--info"
                  style={{ flexDirection: "column", alignItems: "flex-start", gap: 2 }}
                >
                  <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Recomendación
                  </strong>
                  <span style={{ color: "var(--c-text)" }}>{ins.recomendacion}</span>
                </div>
                <div className="hstack" style={{ flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  {ins.entidades.map((e) => (
                    <Link key={e.to + e.ref} to={e.to} className="btn btn--ghost">
                      {e.tipo}: {e.ref} <Icon name="chevron-right" size={11} />
                    </Link>
                  ))}
                </div>
              </div>

              <div style={{ minWidth: 180, textAlign: "right" }}>
                {ins.impacto_clp_mes !== null && (
                  <div style={{ marginBottom: 6 }}>
                    <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
                      Impacto
                    </div>
                    <div className="kpi__value" style={{ fontSize: 22 }}>
                      {clp(ins.impacto_clp_mes)}
                      <span className="text-muted" style={{ fontSize: 12 }}>/mes</span>
                    </div>
                  </div>
                )}
                {ins.impacto_m3_anio !== null && (
                  <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
                    {ins.impacto_m3_anio.toLocaleString("es-CL")} m³/año
                  </div>
                )}
                <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
                  Confianza
                </div>
                <div className="kpi__value" style={{ fontSize: 18, marginBottom: 6 }}>
                  {Math.round(ins.confianza * 100)}%
                </div>
                <Sparkline
                  values={ins.tendencia}
                  height={34}
                  stroke={
                    ins.severidad === "critica"
                      ? "var(--c-danger)"
                      : ins.severidad === "alta"
                        ? "var(--c-warn)"
                        : "var(--c-primary-300)"
                  }
                  fill="rgba(255,255,255,0.04)"
                />
              </div>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

/**
 * Sección de Insights del Motor_Insights (Capa Operacional AI-First).
 *
 * Renderiza los Insights producidos por correlación cross-domain sobre el
 * Grafo_Entidades con su Narrativa_Operacional, evidencia, confianza y
 * Cadena_Causal (R5.2, R5.3, R5.7), e incluye una lectura de KPI
 * intelligence sobre la confianza agregada del periodo (R5.5).
 */
function MotorInsightsSection({
  insights,
  confianzaActualPct,
}: {
  insights: import("../api/contracts").Insight[];
  confianzaActualPct: number;
}): JSX.Element {
  // KPI intelligence: variación de la confianza media frente a una línea
  // base operacional (50%), calculada por el motor vía runtime selector.
  const kpi = kpiIntelligence("Confianza media de Insights", 50, Math.round(confianzaActualPct));

  return (
    <section className="card" style={{ background: "var(--c-surface-2)" }}>
      <div className="card__title">
        <span className="hstack" style={{ gap: 6 }}>
          <Icon name="flask" size={14} /> Motor_Insights · correlación cross-domain
        </span>
        <span className="text-muted text-mono">{insights.length} insights</span>
      </div>
      <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Correlaciones sobre ≥2 dominios (consumo, presión, MNF, NRW, RF, anomalías IoT) por DMA,
        con causalidad y evidencia derivadas de los datos reales de Antofagasta.
      </p>

      {kpi && (
        <div
          className="status-block"
          style={{
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
            marginBottom: 12,
            background: "var(--c-surface)",
            borderColor: "var(--c-border)",
          }}
        >
          <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            KPI intelligence · {kpi.kpi}
          </strong>
          <span style={{ color: "var(--c-text)", fontSize: 12.5 }}>
            {kpi.direccion === "incremento" ? "▲" : "▼"} {kpi.magnitud_pct.toFixed(1)}% · {kpi.causa_probable}
          </span>
        </div>
      )}

      <div className="vstack">
        {insights.map((ins) => (
          <InsightCard key={ins.insight_id} insight={ins} />
        ))}
      </div>
    </section>
  );
}
